import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { Plus, X, Share2, ChevronLeft, ChevronRight, MessageCircle, Check, Trash2, Package, AlertTriangle, Banknote, Smartphone, Camera, Settings, Tag, ShoppingCart } from 'lucide-react';
import api from '../../api';
import { buildCategoryOptions, categoryDepth, MAX_CATEGORY_DEPTH } from '../../utils/categoryTree';

const fmt = (n) => `$${(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const parseUTC = (iso) => {
  if (!iso) return null;
  if (!iso.endsWith('Z') && !/[+-]\d{2}:?\d{2}$/.test(iso)) return new Date(iso.replace(' ', 'T') + 'Z');
  return new Date(iso);
};
const fmtDate = (iso) => {
  if (!iso) return '-';
  const d = parseUTC(iso);
  return d ? d.toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) : '-';
};

const EMPTY_FORM = { name: '', description: '', price: '', stock_id: '', photos: [], is_available: true, category_id: '' };

export default function Catalog() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cartOrders, setCartOrders] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catalogSettings, setCatalogSettings] = useState({ shipping_cost: 0 });
  const [loading, setLoading] = useState(false);

  // Product form modal
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [savingProduct, setSavingProduct] = useState(false);
  const fileInputRef = useRef(null);

  // Sell modal
  const [showSellModal, setShowSellModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedCartOrder, setSelectedCartOrder] = useState(null);
  const [payMethod, setPayMethod] = useState('');
  const [selling, setSelling] = useState(false);

  // Delete confirm
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [cancelOrder, setCancelOrder] = useState(null);
  const [cancelCartOrder, setCancelCartOrder] = useState(null);

  // Category management
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryParentId, setNewCategoryParentId] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);

  // Settings
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ shipping_cost: '' });
  const [savingSettings, setSavingSettings] = useState(false);

  const loadProducts = useCallback(async () => {
    if (!user?.id) return;
    try {
      const r = await api.get(`/catalog-products/${user.id}`);
      setProducts(r.data);
    } catch (e) { console.error(e); }
  }, [user?.id]);

  const loadOrders = useCallback(async () => {
    if (!user?.id) return;
    try {
      const [r1, r2] = await Promise.all([
        api.get(`/catalog-orders/${user.id}`),
        api.get(`/catalog-cart-orders/${user.id}`),
      ]);
      setOrders(r1.data.filter(o => o.status === 'pending'));
      setCartOrders(r2.data.filter(o => o.status === 'pending'));
    } catch (e) { console.error(e); }
  }, [user?.id]);

  const loadStock = useCallback(async () => {
    if (!user?.id) return;
    try {
      const r = await api.get(`/stock/${user.id}`);
      setStockItems(r.data);
    } catch (e) { console.error(e); }
  }, [user?.id]);

  const loadCategories = useCallback(async () => {
    if (!user?.id) return;
    try {
      const r = await api.get(`/catalog-categories/${user.id}`);
      setCategories(r.data);
    } catch (e) { console.error(e); }
  }, [user?.id]);

  const loadSettings = useCallback(async () => {
    if (!user?.id) return;
    try {
      const r = await api.get(`/catalog-settings/${user.id}`);
      setCatalogSettings(r.data);
      setSettingsForm({ shipping_cost: String(r.data.shipping_cost || 0) });
    } catch (e) { console.error(e); }
  }, [user?.id]);

  useEffect(() => {
    loadProducts(); loadOrders(); loadStock(); loadCategories(); loadSettings();
  }, [loadProducts, loadOrders, loadStock, loadCategories, loadSettings]);

  const shareLink = () => {
    const url = `${window.location.origin}/catalogo/${user?.slug}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => alert('¡Link copiado! Compartilo con tus clientes.'));
    } else {
      alert(`Tu link de catálogo: ${url}`);
    }
  };

  // ── Product CRUD ─────────────────────────────

  const openCreate = () => {
    setEditingProduct(null);
    setForm(EMPTY_FORM);
    setShowProductModal(true);
  };

  const openEdit = (p) => {
    setEditingProduct(p);
    setForm({ name: p.name, description: p.description || '', price: String(p.price), stock_id: p.stock_id || '', photos: p.photos || [], is_available: p.is_available, category_id: p.category_id || '' });
    setShowProductModal(true);
  };

  const addPhoto = (e) => {
    const files = Array.from(e.target.files).slice(0, 6 - form.photos.length);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = ev => setForm(f => ({ ...f, photos: [...f.photos, ev.target.result] }));
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (i) => setForm(f => ({ ...f, photos: f.photos.filter((_, idx) => idx !== i) }));

  const saveProduct = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price) { alert('Nombre y precio son obligatorios'); return; }
    setSavingProduct(true);
    try {
      const payload = { name: form.name, description: form.description || null, price: parseFloat(form.price), stock_id: form.stock_id || null, photos: form.photos, is_available: form.is_available, category_id: form.category_id || null };
      if (editingProduct) {
        await api.put(`/catalog-products/${editingProduct.id}`, payload);
      } else {
        await api.post('/catalog-products', { business_user_id: user.id, ...payload });
      }
      setShowProductModal(false);
      loadProducts();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setSavingProduct(false); }
  };

  const confirmDeleteProduct = async () => {
    try {
      await api.delete(`/catalog-products/${deleteProduct.id}`);
      setDeleteProduct(null);
      loadProducts();
    } catch { alert('Error al eliminar'); }
  };

  // ── Categories ────────────────────────────────

  const saveCategory = async () => {
    if (!newCategoryName.trim()) return;
    setSavingCategory(true);
    try {
      await api.post('/catalog-categories', { business_user_id: user.id, name: newCategoryName.trim(), parent_id: newCategoryParentId || null });
      setNewCategoryName('');
      setNewCategoryParentId('');
      loadCategories();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setSavingCategory(false); }
  };

  const confirmDeleteCategory = async (cat) => {
    setDeletingCategory(cat.id);
    try {
      await api.delete(`/catalog-categories/${cat.id}`);
      loadCategories();
      loadProducts();
    } catch { alert('Error al eliminar'); }
    finally { setDeletingCategory(null); }
  };

  // ── Settings ───────────────────────────────────

  const saveSettings = async (e) => {
    e.preventDefault();
    setSavingSettings(true);
    try {
      await api.put(`/catalog-settings/${user.id}`, { shipping_cost: parseFloat(settingsForm.shipping_cost) || 0 });
      await loadSettings();
      setShowSettingsModal(false);
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setSavingSettings(false); }
  };

  // ── Orders ────────────────────────────────────

  const openSell = (order, isCart = false) => {
    if (isCart) {
      setSelectedCartOrder(order);
      setSelectedOrder(null);
    } else {
      setSelectedOrder(order);
      setSelectedCartOrder(null);
    }
    setPayMethod('');
    setShowSellModal(true);
  };

  const confirmSell = async () => {
    if (!payMethod) { alert('Seleccioná la forma de pago'); return; }
    setSelling(true);
    try {
      if (selectedCartOrder) {
        await api.put(`/catalog-cart-orders/${selectedCartOrder.id}/sell`, { payment_method: payMethod });
      } else {
        await api.put(`/catalog-orders/${selectedOrder.id}/sell`, { payment_method: payMethod });
      }
      setShowSellModal(false);
      setSelectedOrder(null);
      setSelectedCartOrder(null);
      loadOrders();
      loadProducts();
    } catch (err) { alert(err.response?.data?.detail || 'Error'); }
    finally { setSelling(false); }
  };

  const confirmCancelOrder = async () => {
    try {
      await api.delete(`/catalog-orders/${cancelOrder.id}`);
      setCancelOrder(null);
      loadOrders();
    } catch { alert('Error'); }
  };

  const confirmCancelCartOrder = async () => {
    try {
      await api.delete(`/catalog-cart-orders/${cancelCartOrder.id}`);
      setCancelCartOrder(null);
      loadOrders();
    } catch { alert('Error'); }
  };

  const openWhatsApp = (order, isCart = false) => {
    const phone = order.client_phone.replace(/\D/g, '');
    let msg;
    if (isCart) {
      const itemsText = order.items.map(i => `• ${i.product_name} x${i.quantity} (${fmt(i.line_total)})`).join('\n');
      msg = `Hola ${order.client_name}!\n\nRecibí tu pedido:\n${itemsText}\n\nSubtotal: ${fmt(order.subtotal)}${order.shipping_cost > 0 ? `\nEnvío: ${fmt(order.shipping_cost)}` : ''}\n*Total: ${fmt(order.total_amount)}*\n\n¿Coordinamos la entrega?`;
    } else {
      msg = `Hola ${order.client_name}! Vi tu pedido de "${order.product_name}" (${fmt(order.total_amount)}). ¿Coordinamos la entrega?`;
    }
    window.open(`https://wa.me/549${phone}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const pendingCount = orders.length + cartOrders.length;

  // Group products by category
  const productsByCategory = () => {
    const grouped = {};
    const uncategorized = [];
    products.forEach(p => {
      if (p.category_id && p.category_name) {
        if (!grouped[p.category_id]) grouped[p.category_id] = { name: p.category_name, items: [] };
        grouped[p.category_id].items.push(p);
      } else {
        uncategorized.push(p);
      }
    });
    return { grouped, uncategorized };
  };

  const renderProductCard = (p) => (
    <div key={p.id} className="card-row" data-testid={`catalog-product-${p.id}`} style={{ gap: 12 }}>
      {p.photos.length > 0 ? (
        <img src={p.photos[0]} alt="" style={{ width: 70, height: 70, objectFit: 'cover', borderRadius: 10, flexShrink: 0 }} />
      ) : (
        <div style={{ width: 70, height: 70, borderRadius: 10, background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Package size={28} color="var(--text2)" strokeWidth={1} />
        </div>
      )}
      <div className="card-row-info">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <div className="card-row-title" style={{ fontSize: '15px' }}>{p.name}</div>
          <span style={{ fontSize: '11px', padding: '2px 7px', borderRadius: 4, fontWeight: 700, background: p.is_available && (p.stock_quantity === null || p.stock_quantity > 0) ? 'rgba(78,205,196,0.12)' : 'rgba(255,82,82,0.12)', color: p.is_available && (p.stock_quantity === null || p.stock_quantity > 0) ? '#4ECDC4' : '#ff5252' }}>
            {p.is_available && (p.stock_quantity === null || p.stock_quantity > 0) ? 'Disponible' : 'Sin stock'}
          </span>
        </div>
        {p.description && <div className="card-row-detail" style={{ fontSize: '12px' }}>{p.description}</div>}
        <div style={{ display: 'flex', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 800, color: 'var(--accent)' }}>{fmt(p.price)}</span>
          {p.stock_quantity !== null && <span style={{ fontSize: '12px', color: 'var(--text2)' }}>Stock: {p.stock_quantity}</span>}
        </div>
      </div>
      <div className="card-row-actions">
        <button className="icon-btn-sm" onClick={() => openEdit(p)}>✏️</button>
        <button className="icon-btn-sm danger" onClick={() => setDeleteProduct(p)}>🗑️</button>
      </div>
    </div>
  );

  const renderCartOrder = (o) => (
    <div key={o.id} data-testid={`cart-order-${o.id}`} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <ShoppingCart size={14} color="var(--accent)" />
            <span style={{ fontWeight: 700, fontSize: '16px' }}>{o.client_name}</span>
          </div>
          <div style={{ fontSize: '13px', color: 'var(--text2)', marginTop: 2 }}>📞 {o.client_phone}</div>
          {o.client_address && <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: 2 }}>📍 {o.client_address}</div>}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontWeight: 800, fontSize: '18px', color: 'var(--accent)' }}>{fmt(o.total_amount)}</div>
          <div style={{ fontSize: '11px', color: 'var(--text2)' }}>{fmtDate(o.created_at)}</div>
        </div>
      </div>
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginBottom: 8 }}>
        {o.items.map((item, i) => (
          <div key={i} style={{ fontSize: '13px', color: 'var(--text)', display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
            <span>{item.product_name} × {item.quantity}</span>
            <span style={{ color: 'var(--text2)' }}>{fmt(item.line_total)}</span>
          </div>
        ))}
        {o.shipping_cost > 0 && (
          <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: 4 }}>+ Envío: {fmt(o.shipping_cost)}</div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '12px', background: 'rgba(74,158,255,0.12)', color: '#4A9EFF', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
            {o.payment_preference === 'efectivo' ? 'Efectivo' : 'Transferencia'}
          </span>
          {o.wants_delivery && <span style={{ fontSize: '12px', background: 'rgba(255,152,0,0.12)', color: '#FF9800', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>Con envío</span>}
        </div>
        {o.notes && <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: 4, fontStyle: 'italic' }}>Nota: {o.notes}</div>}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button data-testid={`cart-whatsapp-${o.id}`} onClick={() => openWhatsApp(o, true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 8, background: '#25D366', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}>
          <MessageCircle size={15} /> WhatsApp
        </button>
        <button data-testid={`cart-sell-${o.id}`} onClick={() => openSell(o, true)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 14px', borderRadius: 8, background: 'linear-gradient(145deg, var(--gradient-end), var(--gradient-start))', color: '#000', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}>
          <Check size={15} /> Vendido
        </button>
        <button data-testid={`cart-cancel-${o.id}`} onClick={() => setCancelCartOrder(o)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 8 }}>
          <Trash2 size={16} />
        </button>
      </div>
    </div>
  );

  const { grouped, uncategorized } = productsByCategory();
  const categoryOptions = buildCategoryOptions(categories);

  return (
    <div className="page-full">
      <div className="header">
        <button className="icon-btn" onClick={() => navigate(-1)}>←</button>
        <h1 className="header-title" style={{ flex: 1 }}>Mi Catálogo</h1>
        <button
          data-testid="catalog-settings-btn"
          onClick={() => setShowSettingsModal(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '7px 10px', borderRadius: 8, background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text2)', fontSize: '12px', cursor: 'pointer', marginRight: 6 }}
        >
          <Settings size={15} /> Config.
        </button>
        <button
          data-testid="share-catalog-btn"
          onClick={shareLink}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 10, background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--accent)', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
        >
          <Share2 size={16} /> Compartir
        </button>
      </div>

      {/* Tabs */}
      <div className="period-tabs" style={{ padding: '0 16px', marginBottom: 0 }}>
        <button className={`period-tab ${activeTab === 'products' ? 'active' : ''}`} onClick={() => setActiveTab('products')}>Productos</button>
        <button className={`period-tab ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>
          Pedidos {pendingCount > 0 && <span style={{ marginLeft: 4, background: 'var(--danger)', color: '#fff', borderRadius: 10, padding: '1px 6px', fontSize: 11, fontWeight: 700 }}>{pendingCount}</span>}
        </button>
        <button className={`period-tab ${activeTab === 'categories' ? 'active' : ''}`} onClick={() => setActiveTab('categories')}>
          <Tag size={13} style={{ marginRight: 4 }} />Categorías
        </button>
      </div>

      <div className="content-scroll">
        {/* ── PRODUCTS TAB ── */}
        {activeTab === 'products' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
              <button className="btn-primary" data-testid="add-catalog-product-btn" style={{ width: 'auto', padding: '10px 20px', fontSize: '14px' }} onClick={openCreate}>
                <Plus size={16} style={{ marginRight: 6 }} /> Agregar Producto
              </button>
            </div>
            {catalogSettings.shipping_cost > 0 && (
              <div style={{ background: 'rgba(24,203,150,0.08)', border: '1px solid rgba(24,203,150,0.2)', borderRadius: 10, padding: '8px 14px', marginBottom: 12, fontSize: '12px', color: 'var(--text2)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Smartphone size={13} color="var(--accent)" /> Envío configurado: <strong style={{ color: 'var(--accent)' }}>{fmt(catalogSettings.shipping_cost)}</strong>
              </div>
            )}
            {products.length === 0 && (
              <div className="empty-state">
                <span className="empty-icon"><Package size={52} strokeWidth={1} /></span>
                <p className="empty-text">No hay productos en tu catálogo</p>
                <p className="empty-subtext">Agregá productos para que tus clientes puedan verlos</p>
              </div>
            )}

            {/* Products grouped by category */}
            {Object.values(grouped).map(group => (
              <div key={group.name}>
                <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '12px 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Tag size={12} /> {group.name}
                </div>
                {group.items.map(renderProductCard)}
              </div>
            ))}
            {uncategorized.length > 0 && (
              <div>
                {Object.keys(grouped).length > 0 && (
                  <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '12px 0 8px' }}>Sin categoría</div>
                )}
                {uncategorized.map(renderProductCard)}
              </div>
            )}
          </>
        )}

        {/* ── ORDERS TAB ── */}
        {activeTab === 'orders' && (
          <>
            {orders.length === 0 && cartOrders.length === 0 && (
              <div className="empty-state">
                <span className="empty-icon">📬</span>
                <p className="empty-text">No hay pedidos pendientes</p>
                <p className="empty-subtext">Los pedidos de tus clientes aparecerán aquí</p>
              </div>
            )}
            {cartOrders.map(renderCartOrder)}
            {orders.map(o => (
              <div key={o.id} data-testid={`catalog-order-${o.id}`} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: '16px' }}>{o.client_name}</div>
                    <div style={{ fontSize: '13px', color: 'var(--text2)', marginTop: 2 }}>📞 {o.client_phone}</div>
                    {o.client_address && <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: 2 }}>📍 {o.client_address}</div>}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: '18px', color: 'var(--accent)' }}>{fmt(o.total_amount)}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text2)' }}>{fmtDate(o.created_at)}</div>
                  </div>
                </div>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, marginBottom: 8 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600 }}>{o.product_name} × {o.quantity}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', background: 'rgba(74,158,255,0.12)', color: '#4A9EFF', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>
                      {o.payment_preference === 'efectivo' ? 'Paga efectivo' : 'Paga transferencia'}
                    </span>
                    {o.wants_delivery && <span style={{ fontSize: '12px', background: 'rgba(255,152,0,0.12)', color: '#FF9800', padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}>Con envío +{fmt(o.shipping_cost)}</span>}
                  </div>
                  {o.notes && <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: 4, fontStyle: 'italic' }}>Nota: {o.notes}</div>}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button data-testid={`whatsapp-btn-${o.id}`} onClick={() => openWhatsApp(o)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 8, background: '#25D366', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}>
                    <MessageCircle size={15} /> WhatsApp
                  </button>
                  <button data-testid={`sell-btn-${o.id}`} onClick={() => openSell(o)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px 14px', borderRadius: 8, background: 'linear-gradient(145deg, var(--gradient-end), var(--gradient-start))', color: '#000', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: 700 }}>
                    <Check size={15} /> Vendido
                  </button>
                  <button data-testid={`cancel-order-btn-${o.id}`} onClick={() => setCancelOrder(o)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 8 }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}

        {/* ── CATEGORIES TAB ── */}
        {activeTab === 'categories' && (
          <>
            <div style={{ background: 'rgba(24,203,150,0.07)', border: '1px solid rgba(24,203,150,0.15)', borderRadius: 10, padding: '10px 14px', marginBottom: 14, fontSize: '13px', color: 'var(--text2)' }}>
              Las categorías permiten que tus clientes filtren los productos en el catálogo público. Podés crear hasta {MAX_CATEGORY_DEPTH} niveles de subcategorías (ej: Audio → Auriculares → Bluetooth).
            </div>
            {/* Add category */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input
                className="input"
                placeholder="Nueva categoría o subcategoría..."
                value={newCategoryName}
                onChange={e => setNewCategoryName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && saveCategory()}
                data-testid="new-category-input"
                style={{ flex: 1 }}
              />
              <button
                onClick={saveCategory}
                disabled={savingCategory || !newCategoryName.trim()}
                data-testid="save-category-btn"
                style={{ padding: '10px 16px', borderRadius: 10, background: 'linear-gradient(145deg, var(--gradient-end), var(--gradient-start))', color: '#000', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '14px', flexShrink: 0 }}
              >
                <Plus size={18} />
              </button>
            </div>
            <div className="form-group" style={{ marginBottom: 16 }}>
              <label className="label" style={{ fontSize: '12px' }}>Categoría padre (opcional, para crear subcategoría)</label>
              <select className="input" data-testid="new-category-parent-select" value={newCategoryParentId} onChange={e => setNewCategoryParentId(e.target.value)}>
                <option value="">Categoría principal (sin padre)</option>
                {categoryOptions.filter(c => categoryDepth(categories, c.id) <= MAX_CATEGORY_DEPTH).map(c => (
                  <option key={c.id} value={c.id}>{'—'.repeat(c.depth)} {c.name}</option>
                ))}
              </select>
            </div>
            {categories.length === 0 && (
              <div className="empty-state" style={{ padding: '40px 20px' }}>
                <span className="empty-icon"><Tag size={40} strokeWidth={1} /></span>
                <p className="empty-text">Sin categorías</p>
                <p className="empty-subtext">Crea categorías para organizar tus productos</p>
              </div>
            )}
            {categoryOptions.map(cat => (
              <div key={cat.id} className="card-row" data-testid={`category-${cat.id}`}>
                <div className="card-row-info">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: cat.depth * 14 }}>
                    <Tag size={16} color="var(--accent)" />
                    <span style={{ fontWeight: 600, fontSize: '15px' }}>{cat.depth > 0 && '—'.repeat(cat.depth) + ' '}{cat.name}</span>
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: 3, paddingLeft: cat.depth * 14 }}>
                    {products.filter(p => p.category_id === cat.id).length} producto(s)
                  </div>
                </div>
                <button
                  data-testid={`delete-category-${cat.id}`}
                  onClick={() => confirmDeleteCategory(cat)}
                  disabled={deletingCategory === cat.id}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: 8 }}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </>
        )}
      </div>

      {/* ── SETTINGS MODAL ── */}
      {showSettingsModal && (
        <div className="modal-overlay" onClick={() => setShowSettingsModal(false)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="modal-title" style={{ margin: 0 }}>Configuración del Catálogo</h3>
              <button onClick={() => setShowSettingsModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)' }}><X size={22} /></button>
            </div>
            <form onSubmit={saveSettings}>
              <div className="form-group">
                <label className="label">Precio de envío general</label>
                <p style={{ fontSize: '12px', color: 'var(--text2)', marginBottom: 8 }}>Este precio se aplica a TODOS los pedidos que soliciten envío. El cliente paga una sola vez sin importar la cantidad de productos.</p>
                <input
                  className="input"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0 (sin envío)"
                  value={settingsForm.shipping_cost}
                  onChange={e => setSettingsForm(f => ({ ...f, shipping_cost: e.target.value }))}
                  data-testid="global-shipping-input"
                />
              </div>
              <div className="modal-buttons">
                <button type="button" className="btn-secondary" onClick={() => setShowSettingsModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" data-testid="save-settings-btn" disabled={savingSettings} style={{ flex: 1 }}>
                  {savingSettings ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Product Create/Edit Modal ── */}
      {showProductModal && (
        <div className="modal-overlay" onClick={() => setShowProductModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxHeight: '92vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 className="modal-title" style={{ margin: 0 }}>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</h3>
              <button onClick={() => setShowProductModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text2)' }}><X size={22} /></button>
            </div>
            <form onSubmit={saveProduct}>
              <div className="form-group">
                <label className="label">Nombre *</label>
                <input className="input" data-testid="catalog-product-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="label">Descripción</label>
                <textarea className="input" rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Características del producto..." />
              </div>
              <div className="form-group">
                <label className="label">Precio *</label>
                <input className="input" type="number" step="0.01" data-testid="catalog-product-price" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} required />
              </div>

              {/* Category */}
              {categories.length > 0 && (
                <div className="form-group">
                  <label className="label"><Tag size={14} style={{ marginRight: 6 }} /> Categoría</label>
                  <select className="input" value={form.category_id} onChange={e => setForm(f => ({ ...f, category_id: e.target.value }))} data-testid="catalog-product-category">
                    <option value="">Sin categoría</option>
                    {categoryOptions.map(c => <option key={c.id} value={c.id}>{'—'.repeat(c.depth)} {c.name}</option>)}
                  </select>
                </div>
              )}

              {/* Photos */}
              <div className="form-group">
                <label className="label"><Camera size={14} style={{ marginRight: 6 }} /> Fotos del producto (máx. 6)</label>
                <input type="file" ref={fileInputRef} accept="image/*" multiple onChange={addPhoto} style={{ display: 'none' }} />
                {form.photos.length < 6 && (
                  <button type="button" className="btn-secondary" onClick={() => fileInputRef.current?.click()} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 10 }}>
                    <Camera size={16} /> Agregar fotos
                  </button>
                )}
                {form.photos.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {form.photos.map((photo, i) => (
                      <div key={i} style={{ position: 'relative' }}>
                        <img src={photo} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8 }} />
                        <button type="button" onClick={() => removePhoto(i)} style={{ position: 'absolute', top: -6, right: -6, background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                        {i === 0 && <span style={{ position: 'absolute', bottom: 4, left: 4, fontSize: '9px', background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: 3, padding: '1px 4px' }}>Portada</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Stock link */}
              <div className="form-group">
                <label className="label">📦 Vincular con Stock (opcional)</label>
                <select className="input" value={form.stock_id} onChange={e => setForm(f => ({ ...f, stock_id: e.target.value }))}>
                  <option value="">Sin vincular</option>
                  {stockItems.map(s => <option key={s.id} value={s.id}>{s.description} ({s.quantity} disponibles)</option>)}
                </select>
              </div>

              {/* Available toggle */}
              <label className="checkbox-row" onClick={() => setForm(f => ({ ...f, is_available: !f.is_available }))} style={{ background: 'var(--surface)', padding: '12px', borderRadius: 8, marginBottom: 14 }}>
                <div className={`checkbox-box ${form.is_available ? 'checked' : ''}`}>{form.is_available && <Check size={16} strokeWidth={3} />}</div>
                <span>Disponible para pedir</span>
              </label>

              <div className="modal-buttons">
                <button type="button" className="btn-secondary" onClick={() => setShowProductModal(false)}>Cancelar</button>
                <button type="submit" className="btn-primary" data-testid="save-catalog-product-btn" disabled={savingProduct} style={{ flex: 1 }}>
                  {savingProduct ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Sell Modal ── */}
      {showSellModal && (selectedOrder || selectedCartOrder) && (
        <div className="modal-overlay" onClick={() => setShowSellModal(false)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Registrar Venta</h3>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              {selectedCartOrder ? (
                <>
                  <div style={{ fontSize: '13px', color: 'var(--text2)', marginBottom: 4 }}>
                    {selectedCartOrder.items.map(i => `${i.product_name} x${i.quantity}`).join(', ')}
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--accent)' }}>{fmt(selectedCartOrder.total_amount)}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: 4 }}>Cliente: {selectedCartOrder.client_name}</div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: '14px', color: 'var(--text2)', marginBottom: 4 }}>{selectedOrder.product_name} × {selectedOrder.quantity}</div>
                  <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--accent)' }}>{fmt(selectedOrder.total_amount)}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: 4 }}>Cliente: {selectedOrder.client_name}</div>
                </>
              )}
            </div>
            <div className="form-group">
              <label className="label">Forma de pago *</label>
              <div className="payment-row">
                <button type="button" data-testid="sell-efectivo" className={`payment-btn ${payMethod === 'efectivo' ? 'active' : ''}`} onClick={() => setPayMethod('efectivo')}>
                  <Banknote size={16} /> Efectivo
                </button>
                <button type="button" data-testid="sell-transferencia" className={`payment-btn ${payMethod === 'transferencia' ? 'active' : ''}`} onClick={() => setPayMethod('transferencia')}>
                  <Smartphone size={16} /> Transferencia
                </button>
              </div>
            </div>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setShowSellModal(false)}>Cancelar</button>
              <button data-testid="confirm-sell-btn" className="btn-primary" onClick={confirmSell} disabled={selling} style={{ flex: 1 }}>
                {selling ? 'Registrando...' : 'Confirmar Venta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Product Confirm ── */}
      {deleteProduct && (
        <div className="modal-overlay" onClick={() => setDeleteProduct(null)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Eliminar Producto</h3>
            <p style={{ textAlign: 'center', color: 'var(--text2)', marginBottom: 20 }}>
              ¿Eliminar <strong style={{ color: 'var(--text)' }}>{deleteProduct.name}</strong> del catálogo?<br />Esta acción no se puede deshacer.
            </p>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setDeleteProduct(null)}>Cancelar</button>
              <button className="btn-danger" onClick={confirmDeleteProduct} style={{ flex: 1 }}>Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cancel Order Confirm ── */}
      {cancelOrder && (
        <div className="modal-overlay" onClick={() => setCancelOrder(null)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Cancelar Pedido</h3>
            <p style={{ textAlign: 'center', color: 'var(--text2)', marginBottom: 20 }}>
              ¿Cancelar el pedido de <strong style={{ color: 'var(--text)' }}>{cancelOrder.client_name}</strong>?
            </p>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setCancelOrder(null)}>Atrás</button>
              <button className="btn-danger" onClick={confirmCancelOrder} style={{ flex: 1 }}>Cancelar Pedido</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Cancel Cart Order Confirm ── */}
      {cancelCartOrder && (
        <div className="modal-overlay" onClick={() => setCancelCartOrder(null)}>
          <div className="modal-content modal-center" onClick={e => e.stopPropagation()}>
            <h3 className="modal-title">Cancelar Pedido</h3>
            <p style={{ textAlign: 'center', color: 'var(--text2)', marginBottom: 20 }}>
              ¿Cancelar el pedido de <strong style={{ color: 'var(--text)' }}>{cancelCartOrder.client_name}</strong>?
            </p>
            <div className="modal-buttons">
              <button className="btn-secondary" onClick={() => setCancelCartOrder(null)}>Atrás</button>
              <button className="btn-danger" onClick={confirmCancelCartOrder} style={{ flex: 1 }}>Cancelar Pedido</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
